import { MessageSquare, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Area, NumField } from './ClinicalFieldControls';
import { SectionCard } from './SectionCard';

interface AnamnesisSectionProps {
  anam: Record<string, string>;
  editable: boolean;
  onChange: (key: string, value: string) => void;
  onBlurSave: () => void;
}

// "Historia Clínica" fields, in order — vaccines/deworming/surgeries/last heat, then the
// "Otros" sub-group. All open free text (TEXT columns), same control as the rest of this
// table's notes fields.
const HISTORIA_FIELDS = [
  'vaccination_status',
  'deworming_status',
  'surgeries_notes',
  'last_heat_notes',
] as const;

const HISTORIA_OTROS_FIELDS = [
  'diet_notes',
  'barf_diet_notes',
  'fleas_ticks_notes',
  'cohabitation_notes',
  'street_exposure_notes',
] as const;

/** "Historia Clínica y Anamnesis" tab split into 2 cards: structured clinical history
 *  (vaccines/deworming/surgeries/last heat + an "Otros" sub-group), and a separate open
 *  Anamnesis card (chief complaint + symptom onset + free narrative) — same state, mutation
 *  and onBlur-save wiring as before, only the field set + layout changed. */
export function AnamnesisSection({
  anam,
  editable,
  onChange,
  onBlurSave,
}: AnamnesisSectionProps): JSX.Element {
  const { t } = useTranslation();
  const blur = editable ? onBlurSave : undefined;

  return (
    <div className="space-y-3">
      <SectionCard icon={Shield} title={t('clinical.card_history')}>
        <div className="grid gap-3 md:grid-cols-2">
          {HISTORIA_FIELDS.map((key) => (
            <Area
              key={key}
              label={t(`clinical.f_${key}`)}
              value={anam[key] ?? ''}
              disabled={!editable}
              onChange={(v) => onChange(key, v)}
              onBlur={blur}
              rows={2}
              placeholder={t(`clinical.f_${key}_full`)}
            />
          ))}
        </div>

        <p className="text-muted-foreground pt-1 text-xs font-medium uppercase tracking-wide">
          {t('clinical.card_history_otros')}
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          {HISTORIA_OTROS_FIELDS.map((key) => (
            <Area
              key={key}
              label={t(`clinical.f_${key}`)}
              value={anam[key] ?? ''}
              disabled={!editable}
              onChange={(v) => onChange(key, v)}
              onBlur={blur}
              rows={2}
              placeholder={t(`clinical.f_${key}_full`)}
            />
          ))}
        </div>
      </SectionCard>

      <SectionCard icon={MessageSquare} title={t('clinical.card_reason_and_evolution')}>
        <div className="grid gap-3 md:grid-cols-2">
          <Area
            label={t('clinical.f_chief_complaint')}
            value={anam.chief_complaint ?? ''}
            disabled={!editable}
            onChange={(v) => onChange('chief_complaint', v)}
            onBlur={blur}
            helper={t('clinical.card_reason_helper')}
          />
          <NumField
            label={t('clinical.f_symptom_onset_days')}
            value={anam.symptom_onset_days ?? ''}
            disabled={!editable}
            onChange={(v) => onChange('symptom_onset_days', v)}
            onBlur={blur}
            placeholder="Ej. 3"
          />
        </div>
        <Area
          label={t('clinical.f_other_notes')}
          value={anam.other_notes ?? ''}
          disabled={!editable}
          onChange={(v) => onChange('other_notes', v)}
          onBlur={blur}
          rows={3}
          placeholder={t('clinical.card_observations_placeholder')}
        />
      </SectionCard>
    </div>
  );
}
